import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon, Upload01Icon } from "@hugeicons/core-free-icons";
import axios from "axios";

// Reusable Field Component
const FieldWithLabel = ({ label, value, onChange, multiline = false, type = "text" }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
      {label}
    </Typography>
    <TextField
      fullWidth
      size="small"
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={`Enter ${label.toLowerCase()}`}
      multiline={multiline}
      rows={multiline ? 2 : 1}
      InputLabelProps={type === "date" ? { shrink: true } : {}}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "15px",
          backgroundColor: "white",
        },
      }}
    />
  </Box>
);

const AddBillDetails = ({ open, onClose, onSave, trackerId }) => {
  const [billData, setBillData] = useState({
    bill_number: "",
    bill_name: "",
    description: "",
    amount: "",
    status: "",
    due_date: "",
    date: "",
    image: null,
    imagePreview: null,
  });

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (msg, severity = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handleChange = (field, value) => {
    setBillData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleChange("image", file);
      handleChange("imagePreview", URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    try {
      const formData = new FormData();
      formData.append("bill_number", billData.bill_number);
      formData.append("bill_name", billData.bill_name);
      formData.append("description", billData.description);
      formData.append("amount", billData.amount);
      formData.append("status", billData.status);
      formData.append("due_date", billData.due_date);
      formData.append("date", billData.date);
      formData.append("tracker", trackerId);
      if (billData.image) formData.append("image", billData.image);

      const res = await axios.post(
        `http://127.0.0.1:8000/bills/trackers/${trackerId}/bills/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      showSnackbar("Bill added successfully!", "success");

      // Trigger parent update without refresh
      if (onSave) onSave(res.data);

      // Close dialog after short delay
      setTimeout(() => onClose(), 1200);
    } catch (error) {
      console.error("Error saving bill:", error);
      showSnackbar("Failed to add bill!", "error");
    }
  };

  return (
    <>
      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <DialogContent sx={{ p: 0, backgroundColor: "#ffffff" }}>
          <Box
            sx={{
              background: "#EEF4FF",
              px: 3,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTopLeftRadius: "12px",
              borderTopRightRadius: "12px",
            }}
          >
            <Typography fontWeight="600" fontSize="16px">
              Add Bill Details
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <HugeiconsIcon icon={Cancel01Icon} size={20} color="#344054" />
            </IconButton>
          </Box>

          {/* Form Content */}
          <Box sx={{ px: 3, py: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FieldWithLabel
                  label="Bill No"
                  value={billData.bill_number}
                  onChange={(val) => handleChange("bill_number", val)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FieldWithLabel
                  label="Bill Name"
                  value={billData.bill_name}
                  onChange={(val) => handleChange("bill_name", val)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FieldWithLabel
                  label="Amount"
                  value={billData.amount}
                  onChange={(val) => handleChange("amount", val)}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FieldWithLabel
                  label="Status"
                  value={billData.status}
                  onChange={(val) => handleChange("status", val)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FieldWithLabel
                  label="Bill created date"
                  value={billData.date}
                  onChange={(val) => handleChange("date", val)}
                  type="date"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FieldWithLabel
                  label="Bill due date"
                  value={billData.due_date}
                  onChange={(val) => handleChange("due_date", val)}
                  type="date"
                />
              </Grid>
            </Grid>

            {/* Description */}
            <Box sx={{ mt: 3 }}>
              <FieldWithLabel
                label="About this Bill"
                value={billData.description}
                onChange={(val) => handleChange("description", val)}
                multiline
              />
            </Box>

            {/* Image Upload */}
            <Box sx={{ mt: 3, textAlign: "center" }}>
              {billData.imagePreview ? (
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <img
                    src={billData.imagePreview}
                    alt="Bill Preview"
                    style={{
                      width: "500px",
                      borderRadius: "10px",
                      border: "1px solid #E0E0E0",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      handleChange("image", null);
                      handleChange("imagePreview", null);
                    }}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "white",
                      boxShadow: 1,
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                  >
                    <HugeiconsIcon icon={Cancel01Icon} size={18} color="#d32f2f" />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <Typography sx={{ color: "#999", mb: 2, fontSize: "12px" }}>
                    No bill image uploaded
                  </Typography>
                  <Button
                    component="label"
                    variant="outlined"
                    fullWidth
                    startIcon={<HugeiconsIcon icon={Upload01Icon} size={18} />}
                    sx={{
                      mt: 2,
                      textTransform: "none",
                      borderRadius: "50px",
                      fontWeight: "bold",
                      borderStyle: "dashed",
                      borderColor: "#D9DDE3",
                      backgroundColor: "#F3F5F9",
                      color: "#7A7F88",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                      fontSize: 13,
                    }}
                  >
                    Upload Bill
                    <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                  </Button>
                </>
              )}
            </Box>

            {/* Save Button */}
            <Box sx={{ display: "flex", justifyContent: "end", mt: 3, gap: 2 }}>
              <Button onClick={onClose} sx={{ textTransform: "none", color: "#666" }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  background: "#0949b0",
                  color: "#fff",
                  borderRadius: "100px",
                  textTransform: "none",
                }}
              >
                Save Bill
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddBillDetails;
